import {
  Application,
  FeathersService,
  Paginated,
  Service
} from "@feathersjs/feathers/lib";
import { makeObservable, observable, action } from "mobx";
import client from "src/feathers";
import { DataType, IAgenda, IAgendaSpace, IAgendaStep } from "../types";
import { Data } from "./";

export class Agenda extends Data<IAgenda> implements IAgenda {
  protected readonly space_link_service: FeathersService<
    Application<IAgendaSpace>,
    Service<IAgendaSpace>
  > = client.service("agenda-space");
  id: number = null;
  steps: IAgendaStep[] = null;
  title: string = null;
  spaces?: number[] = [];

  constructor(data: IAgenda) {
    // The super constructr takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(DataType.Agenda, data);

    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually
    makeObservable(this, {
      steps: observable,
      title: observable,
      setSteps: action
    });

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here.
    this.fromData(data);
  }

  public static create(data?: IAgenda): Agenda {
    return new Agenda(data);
  }

  protected fromData(data: IAgenda): void {
    super.fromData(data);
    this.steps = data.steps;
    this.title = data.title;
  }

  public toData(): any {
    return {
      ...super.toData(),
      steps: this.steps ? JSON.stringify(this.steps) : undefined,
      title: this.title ? this.title : undefined
    };
  }

  protected toPatchData(patch: Partial<IAgenda>): any {
    return {
      ...super.toPatchData(patch),
      steps: "steps" in patch ? JSON.stringify(patch.steps) : undefined,
      title: "title" in patch ? patch.title : undefined
    };
  }

  public async linkSpace(space_id: number): Promise<void> {
    try {
      const agenda_space: IAgendaSpace = {
        agenda_id: this.id,
        space_id: space_id,
        workspace_id: this.workspace_id
      };
      await this.space_link_service.create(agenda_space);
    } catch (err) {
      console.error("Failed to create new Agenda", err);
      return;
    }
  }

  public async unlinkSpace(space_id: number): Promise<void> {
    try {
      const agenda_space: Paginated<IAgendaSpace> =
        (await this.space_link_service.find({
          query: {
            space_id: space_id,
            agenda_id: this.id
          }
        })) as unknown as Paginated<IAgendaSpace>;
      for (const as of agenda_space.data) {
        // In case accidentally more than one link was created
        await this.space_link_service.remove(as.id);
      }
    } catch (err) {
      console.error("Failed to unlink Agenda from space", err);
      return;
    }
  }

  public async setSteps(steps: IAgendaStep[]): Promise<void> {
    try {
      await this.set('steps', steps, true);
      return;
    } catch( error) {
      console.error('failed to set steps', error)
      return;
    }
  
  }
  public async addStep(step?: IAgendaStep): Promise<void> {
    const steps = [...this.steps];

    steps.push({
      ...step,
      index: steps.length + 1,
    });

    await this.setSteps(steps);
   
  };

  public async removeStep(step: IAgendaStep): Promise<void> {
    const steps = this.steps.filter((s) => s.index !== step.index);
    await this.setSteps(steps);
  };

  public async setStepDuration (step: IAgendaStep, durationPercentage: number): Promise<void>  {
    const steps = [...this.steps];
    steps.find((s) => s.index === step.index).percentage = durationPercentage;
    await this.setSteps(steps);
  };
}
